import { useState, useEffect } from 'react';
import { Search, Bell, Link2, Copy, Eye, EyeOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AgentConfig } from '../../types/agent';
import { Badge, EmptyState } from '../shared';

export function MCPEndpoints() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, [user]);

  const loadConfig = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'agentConfigs'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setAgentConfig(snapshot.docs[0].data() as AgentConfig);
      }
    } catch (error) {
      console.error('Error loading agent config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleRotateApiKey = async () => {
    if (!agentConfig || !user) return;

    const confirmed = confirm('Are you sure you want to rotate your API key? This will invalidate the current key.');
    if (!confirmed) return;

    try {
      const newApiKey = 'ka_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      await updateDoc(doc(db, 'agentConfigs', agentConfig.id), {
        apiKey: newApiKey,
        updatedAt: new Date()
      });
      await loadConfig();
      alert('API key rotated successfully');
    } catch (error) {
      console.error('Error rotating API key:', error);
      alert('Failed to rotate API key');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">MCP Endpoints</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
          <h1 className="text-3xl font-semibold text-gray-900">MCP Endpoints</h1>
          <p className="text-sm text-gray-600 mt-1">Connect your brand agent to AI platforms via MCP and REST API</p>
        </div>

        {!agentConfig ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12">
            <EmptyState
              icon={Link2}
              title="No agent configured"
              description="Configure your brand agent first to access endpoints"
            />
          </div>
        ) : (
          <div className="space-y-6">
            {/* MCP Endpoint */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 mb-1">MCP Endpoint</h2>
                  <p className="text-sm text-gray-600">
                    Model Context Protocol endpoint for Claude and compatible AI systems
                  </p>
                </div>
                <Badge
                  variant={agentConfig.deploymentStatus.mcp === 'active' ? 'success' : 'critical'}
                >
                  {agentConfig.deploymentStatus.mcp}
                </Badge>
              </div>

              <div className="bg-surface-warm rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase">Endpoint URL</span>
                  <button
                    onClick={() => handleCopy(agentConfig.mcpEndpoint, 'mcp')}
                    className="flex items-center gap-1 text-sm text-t3-primary hover:text-purple-700"
                  >
                    {copiedField === 'mcp' ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <code className="text-sm text-gray-900 font-mono break-all">
                  {agentConfig.mcpEndpoint}
                </code>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 mb-2">
                  <strong>How to use MCP:</strong>
                </p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Add this endpoint to your Claude Desktop config</li>
                  <li>Authenticate using the API key below</li>
                  <li>Your brand agent will be available as a tool in Claude</li>
                </ol>
              </div>
            </div>

            {/* REST API Endpoint */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 mb-1">REST API Endpoint</h2>
                  <p className="text-sm text-gray-600">
                    Standard REST API for custom integrations and applications
                  </p>
                </div>
                <Badge
                  variant={agentConfig.deploymentStatus.rest === 'active' ? 'success' : 'critical'}
                >
                  {agentConfig.deploymentStatus.rest}
                </Badge>
              </div>

              <div className="bg-surface-warm rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase">Endpoint URL</span>
                  <button
                    onClick={() => handleCopy(agentConfig.restApiEndpoint, 'rest')}
                    className="flex items-center gap-1 text-sm text-t3-primary hover:text-purple-700"
                  >
                    {copiedField === 'rest' ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <code className="text-sm text-gray-900 font-mono break-all">
                  {agentConfig.restApiEndpoint}
                </code>
              </div>

              <div className="bg-surface-warm rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Example Request</p>
                <pre className="text-xs text-gray-900 font-mono overflow-x-auto">
{`POST ${agentConfig.restApiEndpoint}/query
Content-Type: application/json
Authorization: Bearer YOUR_API_KEY

{
  "query": "What are your product features?",
  "context": {
    "userId": "user_123",
    "sessionId": "session_456"
  }
}`}
                </pre>
              </div>
            </div>

            {/* API Credentials */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 mb-1">API Credentials</h2>
                  <p className="text-sm text-gray-600">
                    Use this API key to authenticate requests to your brand agent
                  </p>
                </div>
                <button
                  onClick={handleRotateApiKey}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Rotate Key
                </button>
              </div>

              <div className="bg-surface-warm rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase">API Key</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {showApiKey ? (
                        <>
                          <EyeOff className="w-4 h-4 inline" /> Hide
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 inline" /> Show
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleCopy(agentConfig.apiKey, 'apiKey')}
                      className="flex items-center gap-1 text-sm text-t3-primary hover:text-purple-700"
                    >
                      {copiedField === 'apiKey' ? (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <code className="text-sm text-gray-900 font-mono break-all">
                  {showApiKey ? agentConfig.apiKey : '••••••••••••••••••••••••••••••••'}
                </code>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-900">
                  <strong>⚠️ Security Notice:</strong> Keep your API key secure. Do not share it publicly or commit it to version control.
                </p>
              </div>
            </div>

            {/* Documentation */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Documentation & Resources</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>
                  <a href="#" className="hover:underline">📖 MCP Integration Guide</a>
                </li>
                <li>
                  <a href="#" className="hover:underline">📖 REST API Reference</a>
                </li>
                <li>
                  <a href="#" className="hover:underline">💻 Code Examples (Python, JavaScript, curl)</a>
                </li>
                <li>
                  <a href="#" className="hover:underline">🔐 Authentication Best Practices</a>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
